import { useRecoilValue, useSetRecoilState } from "recoil"
import { annotationGroupsAtom, annotationsAtom, activeAnnotationSessionAtom } from "../Pages/Data/Visualize/DataReview/Atoms/Annotations"
import { useEndpointProvider } from "../Providers/EndpointProvider"
import { Annotation } from "../Managers/VisualizationManager/Variables/Annotations"
import { useAuthProvider } from "../Providers/AuthProvider"
import { AnnotationGroup, AnnotationJSON } from "../Pages/Data/Visualize/DataReview/Types/Annotations"
import { currentPatientFileInfoAtom } from "../Pages/Data/Visualize/DataReview/Atoms/PatientFile"
import { useCallback } from "react"
import { DataSource } from "../Pages/Data/Visualize/DataReview/Types/DataSource"
import { useBackendLinksProvider } from "../Providers/BackendLinksProvider"
import { EnvironmentVariables, useEnvironmentVariablesProvider } from "../Providers/EnvironmentVariablesProvider"

const VIRTUAL_ANNOTATION_GROUP_FEATURE_FLAGS: Record<string, keyof EnvironmentVariables> = {
	"CNS Envision Events": "FEATURE_FLAG_CNS_ENVISION_EVENTS",
	"CNS Monitor Marks": "FEATURE_FLAG_CNS_MONITOR_MARKS",
}

export const filterVirtualAnnotationGroups = (annotationGroups: AnnotationGroup[], environmentVariables: EnvironmentVariables) => {
	return annotationGroups.filter(group => {
		const featureFlag = VIRTUAL_ANNOTATION_GROUP_FEATURE_FLAGS[group.group_name]
		return !(group.read_only === true && featureFlag && environmentVariables[featureFlag] === false)
	})
}

export const useAnnotationService = () => {
	const endpointProvider = useEndpointProvider()
	const currentAnnotationSession = useRecoilValue(activeAnnotationSessionAtom)
	const patientInfo = useRecoilValue(currentPatientFileInfoAtom)

	const authProvider = useAuthProvider()

	const setAnnotations = useSetRecoilState(annotationsAtom)
	const setAnnotationGroups = useSetRecoilState(annotationGroupsAtom)

	const { LINKS } = useBackendLinksProvider()
	const { environmentVariables } = useEnvironmentVariablesProvider()

	async function createAnnotation(annotation: Annotation): Promise<Annotation> {
		if (!currentAnnotationSession) {
			throw new Error("Annotation Session must be selected before creating an annotation.")
		}
		if (currentAnnotationSession.read_only) {
			throw new Error("Annotations cannot be created in a read-only session.")
		}

		const newAnnotation = Annotation.fromConfig(annotation)
		newAnnotation.group_id = currentAnnotationSession.id
		newAnnotation.loaded = true
		newAnnotation.user_id = authProvider.currentUser.id
		newAnnotation.user_email = authProvider.currentUser.email

		let user_full_name = "Unknown"

		if (authProvider.currentUser.first_name?.trim() || authProvider.currentUser.last_name?.trim()) {
			const nameParts = [authProvider.currentUser.first_name, authProvider.currentUser.last_name].filter(part => part && part.trim() !== "")
			user_full_name = nameParts.join(" ")
		}

		newAnnotation.user_full_name = user_full_name

		await endpointProvider
			.post(LINKS.DATA.PROFILING.ADD_ANNOTATION, {
				annotation_group_id: currentAnnotationSession.id,
				annotation: newAnnotation.toJSON()
			})
			.then(storedAnnotationJSON => {
				const json = JSON.parse(storedAnnotationJSON)
				newAnnotation.id = parseInt(json.id)
				setAnnotations(previous => [...previous, newAnnotation])
				setAnnotationGroups(previous =>
					previous.map(group => {
						if (group.id === currentAnnotationSession.id) {
							return { ...group, nannotations: group.nannotations + 1 }
						}
						return group
					})
				)
			})

		return newAnnotation
	}

	async function modifyAnnotation(annotation: Annotation, data: any): Promise<void> {
		if (!currentAnnotationSession) {
			throw new Error("Annotation Session must be selected before creating an annotation.")
		}

		const modifiedAnnotation = new Annotation()
		Object.assign(modifiedAnnotation, annotation)
		Object.assign(modifiedAnnotation, data)

		const body = {
			annotation: modifiedAnnotation.toJSON(),
		}

		return endpointProvider
			.post(LINKS.DATA.PROFILING.MODIFY_ANNOTATION, body)
			.then(() => setAnnotations(previous => previous.map(a => (a.id === annotation.id ? modifiedAnnotation : a))))
	}

	async function deleteAnnotations(annotationIds: number[]) {
		const body = {
			annotation_ids: annotationIds,
		}

		return endpointProvider
			.post(LINKS.DATA.PROFILING.DELETE_ANNOTATIONS, body)
			.then(() => getAnnotationGroups())
			.then(groups => {
				setAnnotations(previous => previous.filter(annotation => !annotationIds.includes(annotation.id)))
				setAnnotationGroups(groups)
			})
	}

	async function getAnnotationGroups() {
		return endpointProvider.post(LINKS.DATA.PROFILING.GET_ANNOTATION_GROUPS, {
			data_object_id: patientInfo.dataSourceMap.get(DataSource.CURRENT_PATIENT)
		}).then((groups: AnnotationGroup[]) => filterVirtualAnnotationGroups(groups, environmentVariables))
	}

	async function deleteGroupAnnotations(annotationGroup: AnnotationGroup) {
		return endpointProvider
			.post(LINKS.DATA.PROFILING.GET_ANNOTATIONS, {
				annotation_group_ids: [annotationGroup.id],
			})
			.then((response: Record<string, AnnotationJSON[]>) => Object.values(response).flat().map(annotation => annotation.id))
			.then(annotationIds => deleteAnnotations(annotationIds))
			.then(() => getAnnotationGroups())
			.then(groups => {
				setAnnotations(previous => [...previous].filter(annotation => annotation.group_id.toString() !== annotationGroup.id.toString()))
				setAnnotationGroups(groups)
			})
	}

	async function deleteAnnotationGroup(annotationGroup: AnnotationGroup) {
		return deleteGroupAnnotations(annotationGroup)
			.then(() =>
				endpointProvider.post(LINKS.DATA.PROFILING.DELETE_ANNOTATION_GROUP, {
					annotation_group_id: annotationGroup.id,
				})
			)
			.then(() => setAnnotationGroups(previous => [...previous].filter(group => group.id !== annotationGroup.id)))
	}

	async function deleteAnnotationGroups(annotationGroups: AnnotationGroup[]) {
		return endpointProvider
			.post(LINKS.DATA.PROFILING.DELETE_ANNOTATION_GROUPS, {
				annotation_group_ids: annotationGroups.map(group => group.id),
			})
			.then(() => {
				const deletedGroupIds = new Set(annotationGroups.map(group => group.id.toString()))
				// Groups first: the session editor refetches when annotations change, and must already see its session gone.
				setAnnotationGroups(previous => previous.filter(group => !deletedGroupIds.has(group.id.toString())))
				setAnnotations(previous => previous.filter(annotation => !deletedGroupIds.has(annotation.group_id.toString())))
			})
	}

	async function loadAnnotationGroups(groupIds: Array<string | number>) {
		return endpointProvider
			.post(LINKS.DATA.PROFILING.GET_ANNOTATIONS, {
				annotation_group_ids: groupIds,
			})
			.then((response: object) => {
				const annotations: Annotation[] = Object.values(response)
					.flat()
					.flatMap((json: AnnotationJSON) => {
						const annotation = Annotation.fromJSON(json)
						annotation.loaded = true
						return annotation
					})

				loadAnnotations(annotations)
			})
	}

	function unloadAnnotationGroups(groupIds: (string | number)[]) {
		const groupIdSet = new Set(groupIds.map(id => id.toString()))
		setAnnotations(previous => [...previous].filter(annotation => !groupIdSet.has(annotation.group_id.toString())))
	}

	async function createAnnotationGroup(groupName: string, dataObjectId: number) {
		return endpointProvider
			.post(LINKS.DATA.PROFILING.CREATE_ANNOTATION_GROUP, {
				data_object_id: dataObjectId,
				annotation_group_name: groupName,
				user_id: authProvider.currentUser.id,
			})
			.then((json: string) => {
				const data: { id: string | number; timestamp: number } = JSON.parse(json)
				const newAnnotationGroup: AnnotationGroup = { id: data.id, timestamp: new Date(data.timestamp), group_name: groupName, nannotations: 0 }
				setAnnotationGroups(previous => [...previous, newAnnotationGroup])
				return newAnnotationGroup
			})
	}

	async function renameAnnotationGroup(annotationGroup: AnnotationGroup, newName: string) {
		return endpointProvider
			.post(LINKS.DATA.PROFILING.RENAME_ANNOTATION_GROUP, {
				annotation_group_id: annotationGroup.id,
				new_group_name: newName,
			})
			.then(() => setAnnotationGroups(previous => previous.map(group => (group.id === annotationGroup.id ? { ...group, group_name: newName } : group))))
	}

	// There's no good reason why this needs to be here. We can just check to see if the annotation is rendered.
	function withLoadedState(annotation: Annotation, loadedState: boolean) {
		const newAnnotation = new Annotation()
		Object.assign(newAnnotation, annotation)
		newAnnotation.loaded = loadedState
		return newAnnotation
	}

	function loadAnnotations(annotations: Annotation[]) {
		setAnnotations(previous => {
			const existingAnnotationIds = previous.map(annotation => annotation.id)
			const newLoadedAnnotations = annotations.filter(annotation => !existingAnnotationIds.includes(annotation.id))
			return [...previous, ...newLoadedAnnotations].map(annotation => withLoadedState(annotation, true))
		})
	}

	function unloadAnnotations(annotations: Annotation[]) {
		const annotationIds = annotations.map(annotation => annotation.id)
		setAnnotations(previous => [...previous].filter(annotation => !annotationIds.includes(annotation.id)))
	}

	const getAnnotationsForGroups = useCallback(
		(annotationGroupIds: Array<string | number>): Promise<Annotation[]> => {
			const body = {
				annotation_group_ids: annotationGroupIds,
			}

			return endpointProvider.post(LINKS.DATA.PROFILING.GET_ANNOTATIONS, body).then((response: object) => {

				const annotations = Object.values(response)
					.flat()
					.flatMap((json: AnnotationJSON) => Annotation.fromJSON(json))

				return annotations
		})
		},
		[LINKS.DATA.PROFILING.GET_ANNOTATIONS, endpointProvider]
	)

	return {
		createAnnotation,
		createAnnotationGroup,
		deleteAnnotationGroup,
		deleteAnnotationGroups,
		loadAnnotations,
		unloadAnnotations,
		loadAnnotationGroups,
		unloadAnnotationGroups,
		modifyAnnotation,
		deleteAnnotations,
		getAnnotationsForGroups,
		getAnnotationGroups,
		renameAnnotationGroup
	}
}
